import {
  MemberServiceOfferingType,
  MemberServiceOfferingUsage,
} from "@/services/core-api-adapter";
import { defaultFullscreenPageStyling } from "@/theme";
import {
  formatDateToReadableString,
  mapServiceOfferingUsageItemsForTranslationKeys,
} from "@/utils";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BottomFixedCallToAction from "../BottomFixedCallToAction";
import ButtonWithAnalytics from "../ButtonWithAnalytics";
import CircledBackButton from "../CircledBackButton";
import IconLoader from "../IconLoader";
import Panel from "../Panel";

interface VoucherSelectionProps {
  onBackButtonClick: () => void;
  onContinueButtonClick: (selectedVoucher: string | undefined) => void;
  serviceOfferingUsageItems: MemberServiceOfferingUsage[];
}

export default function VoucherSelection({
  onBackButtonClick,
  onContinueButtonClick,
  serviceOfferingUsageItems,
}: VoucherSelectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedVoucher, setSelectedVoucher] = useState<any>(
    serviceOfferingUsageItems[0]
  );

  function handleVoucherValueChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const selectedId = event.target.value;
    const selectedItem = serviceOfferingUsageItems.find(
      (item) => item.id === selectedId
    );
    setSelectedVoucher(selectedItem);
  }

  function handleContinueButtonClick() {
    onContinueButtonClick(selectedVoucher);
  }

  function onVoucherBuyButtonClick() {
    navigate("/home/get-medical-assistance/vouchers");
  }

  return (
    <Stack sx={{ ...defaultFullscreenPageStyling }} spacing={4}>
      <CircledBackButton onClick={onBackButtonClick} />
      {serviceOfferingUsageItems.length > 0 ? (
        <Stack spacing={3}>
          <Typography variant="h2" component="h1">
            {t("VoucherSelection.title")}
          </Typography>
          <Panel>
            <FormControl>
              <RadioGroup
                aria-label={t("VoucherSelection.title")}
                value={selectedVoucher?.id}
                onChange={handleVoucherValueChange}
              >
                {serviceOfferingUsageItems.map(
                  (serviceOfferingUsageItem, index) => (
                    <React.Fragment key={serviceOfferingUsageItem.id + index}>
                      <FormControlLabel
                        value={serviceOfferingUsageItem.id}
                        control={<Radio />}
                        label={
                          <Typography>
                            {t(
                              `AvailableVoucherCodesModal.${mapServiceOfferingUsageItemsForTranslationKeys(serviceOfferingUsageItem)}.label`
                            )}
                            {serviceOfferingUsageItem.serviceOfferingType ===
                              MemberServiceOfferingType.PATHOLOGY && (
                              <>
                                :{" "}
                                {t(
                                  `PathologyOrderSummary.${serviceOfferingUsageItem.chargeCode}.title` as any
                                )}
                              </>
                            )}
                          </Typography>
                        }
                      />
                      <Typography
                        variant="body2"
                        textAlign="left"
                        marginLeft={4}
                        fontWeight="400"
                        color="neutral.700"
                      >
                        {t("VoucherSelection.option.date", {
                          date: formatDateToReadableString(
                            new Date(serviceOfferingUsageItem.availableTo)
                          ),
                        })}
                      </Typography>
                    </React.Fragment>
                  )
                )}
              </RadioGroup>
            </FormControl>
          </Panel>
          <BottomFixedCallToAction
            buttonText="common.continueButton"
            onButtonClick={handleContinueButtonClick}
            ariaLabel="common.continueButton"
            analyticsPageName="Voucher Selection Screen"
          />
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Typography variant="h2" component="h1">
            {t("VoucherSelection.empty.title")}
          </Typography>
          <Box
            display="flex"
            gap={2}
            sx={{
              backgroundColor: "accent2.lighter",
              padding: 2,
              borderRadius: 2,
            }}
          >
            <IconLoader
              icon="InformationSymbolFilledCircleIcon"
              color="primary"
            />
            <Typography variant="body1">
              {t("VoucherSelection.empty.subtitle")}
            </Typography>
          </Box>
          <ButtonWithAnalytics
            page="Voucher Selection Screen"
            text={t("VoucherSelection.empty.button")}
            intent="confirmational"
            onClick={onVoucherBuyButtonClick}
            color="primary"
            fullWidth
            variant="outlined"
          >
            {t("VoucherSelection.empty.button")}
          </ButtonWithAnalytics>
        </Stack>
      )}
    </Stack>
  );
}
